import React, { useState } from 'react';
import SvgIcon from '@material-ui/core/SvgIcon';
import { fade, makeStyles, withStyles } from '@material-ui/core/styles';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import DocSearchDynamicGroupChoiceRow from './DocSearchDynamicGroupChoiceRow';
import DocSearchDynamicGroup from './DocSearchDynamicGroup';

function MinusSquare(props) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
    </SvgIcon>
  );
}

function PlusSquare(props) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
}

function CloseSquare(props) {
  return (
    <SvgIcon className="close" fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
      <path d="M17.485 17.512q-.281.281-.682.281t-.696-.268l-4.12-4.147-4.12 4.147q-.294.268-.696.268t-.682-.281-.281-.682.294-.669l4.12-4.147-4.12-4.147q-.294-.268-.294-.669t.281-.682.682-.281.696 .268l4.12 4.147 4.12-4.147q.294-.268.696-.268t.682.281 .281.669-.294.682l-4.12 4.147 4.12 4.147q.294.268 .294.669t-.281.682zM22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0z" />
    </SvgIcon>
  );
}

const useStyles = makeStyles({
  root: {
    height: 400,
    flexGrow: 1,
    maxWidth: 500,

    // Target the correct classes that actually have hover effects
    '& .MuiTreeItem-root': {
      '&:hover > .MuiTreeItem-content > .MuiTreeItem-label': {
        backgroundColor: 'transparent !important',
      },
      '&:focus > .MuiTreeItem-content > .MuiTreeItem-label': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected > .MuiTreeItem-content > .MuiTreeItem-label': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:focus > .MuiTreeItem-content > .MuiTreeItem-label': {
        backgroundColor: 'transparent !important',
      },
    },
    
    // Direct targeting of label class
    '& .MuiTreeItem-label': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
    },

    // Also target content just in case
    '& .MuiTreeItem-content': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
    },
  },
});

const StyledTreeItem = withStyles((theme) => ({
  iconContainer: {
    '& .close': {
      opacity: 0.3,
    },
  },
  group: {
    marginLeft: 7,
    paddingLeft: 18,
    borderLeft: `1px dashed ${fade(theme.palette.text.primary, 0.4)}`,
  },
  label: {
    cursor: 'pointer',
  }
}))((props) => {
  const { onLabelClick, forceExpandable, ...otherProps } = props;
  
  return (
    <TreeItem 
      {...otherProps}
      onLabelClick={(event) => {
        event.preventDefault(); // Prevent default expand/collapse
        event.stopPropagation(); // Stop event bubbling
        if (onLabelClick) {
          onLabelClick(event, props.nodeId);
        }
      }}
    >
      {/* Add dummy child if forceExpandable and no real children */}
      {forceExpandable && !props.children && (
        <div style={{ display: 'none' }} />
      )}
      {props.children}
    </TreeItem>
  );
});

export default function DocDynamicGroupTree(props) {
  const { dynamicGroupManager } = props;
  const classes = useStyles();
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(['']);
  const [popupName, setPopupName] = React.useState('');
  const [anchorRef, setAnchorRef] = React.useState(null);
  const [expandedNodeInfo, setExpandedNodeInfo] = useState(null);

  // Remove the cached nodeLookup
  // const nodeLookup = createNodeLookup(dynamicGroupManager.dynamicGroupChoiceData || {});

  // Real-time lookup function - no caching
const getNodeWithLevel = (nodeId) => {
  const findNodeWithLevel = (nodes, targetId, level = 0, parentPath = '') => {
    for (const node of Object.values(nodes)) {
      const currentNodeId = createNodeId(parentPath, node.rowKey);
      
      if (currentNodeId === targetId) {
        return { ...node, level, dynamicNodeId: currentNodeId };
      }
      
      if (node.children) {
        const found = findNodeWithLevel(node.children, targetId, level + 1, currentNodeId);
        if (found) return found;
      }
    }
    return null;
  };
  
  return findNodeWithLevel(dynamicGroupManager.dynamicGroupChoiceData || {}, nodeId);
};

  // Get root nodes (top-level nodes)
  const getRootNodes = () => {
    return Object.values(dynamicGroupManager.dynamicGroupChoiceData || {});
  };

const buildNodePath = (targetNodeId) => {
  const findPath = (nodes, target, path = [], parentPath = '') => {
    for (const [rowKey, node] of Object.entries(nodes)) {
      const currentNodeId = createNodeId(parentPath, rowKey);
      const currentPath = [...path, currentNodeId];
      
      if (currentNodeId === target) {
        return currentPath.join(',');
      }
      
      if (node.children) {
        const found = findPath(node.children, target, currentPath, currentNodeId);
        if (found) return found;
      }
    }
    return null;
  };
  
  return findPath(dynamicGroupManager.dynamicGroupChoiceData || {}, targetNodeId) || targetNodeId;
};

const handleNodeToggle = (event, nodeIds) => {
    // console.log('Icon clicked - expanded nodes:', nodeIds);
    setExpandedNodes(nodeIds);
    
    const newlyExpanded = nodeIds.filter(id => !expandedNodes.includes(id));
    const newlyCollapsed = expandedNodes.filter(id => !nodeIds.includes(id));
    
    if (newlyExpanded.length > 0) {
      const expandedNodeId = newlyExpanded[0];
      const expandedNode = getNodeWithLevel(expandedNodeId);
      
      // console.log('Expanded node:', expandedNodeId, expandedNode);
      // console.log('Node level:', expandedNode?.level);
      
      if (expandedNode) {
        const nodeLevel = expandedNode.level + 1;
        const hasChildren = expandedNode.children && Object.keys(expandedNode.children).length > 0;
        const hasStoredSelection = dynamicGroupManager.hasGroupSelectionForLevel(nodeLevel);
        
        // Build node path from root to expanded node
        const nodePath = buildNodePath(expandedNodeId);

      // console.log('Node path toggle:', expandedNodeId, nodePath, nodeLevel);

      if (hasChildren) {
          // Scenario 2: Node has children - expand directly (no menu)
          // console.log('Scenario 2: Node has children - expanding directly');
          // No popup needed, just expand
        } else if (hasStoredSelection) {
          // Scenario 3: Use stored selection - no menu, trigger server request
          // console.log('Scenario 3: Using stored selection for level:', nodeLevel);
          dynamicGroupManager.onGroupSelect(nodeLevel, nodePath, null);
        } else {
          // Scenario 1: First-time expansion - show menu popup
          // console.log('Scenario 1: First-time expansion - showing menu');
          setAnchorRef(event.currentTarget);
          setPopupName("showDynamicGroupMenu");
          setExpandedNodeInfo({ nodeId: expandedNodeId, level: nodeLevel, nodePath: nodePath });
        }
      }
    }
    
    if (newlyCollapsed.length > 0) {
      // Hide popup when collapsing
      setPopupName("");
      setAnchorRef(null);
      setExpandedNodeInfo(null);
      
      // const collapsedNodeId = newlyCollapsed[0];
      // const collapsedNode = getNodeWithLevel(collapsedNodeId);
      // console.log('Collapsed node:', collapsedNodeId, collapsedNode);
    }
};

const handleNodeContentClick = (event, nodeId) => {
    // console.log('Node CONTENT clicked:', nodeId);
    
    const nodeData = getNodeWithLevel(nodeId); // Real-time lookup
    // console.log('Node data:', nodeId, nodeData, nodeData?.level);
    
    setSelectedNode(nodeId);
    
    // Build the node path for the clicked node
    const nodePath = buildNodePath(nodeId);
    // console.log('Node path clicked:', nodeId, nodePath, nodeData.level);
    
    // Call onGroupChoiceChange with the nodePath
    if (dynamicGroupManager.onGroupChoiceChange) {
        dynamicGroupManager.onGroupChoiceChange(nodePath, nodeData.level);
    }
};

  const shouldShowExpandIcon = (node) => {
    return (
      (node.children && Object.keys(node.children).length > 0) || // Has children
      node.canHaveChildren ||                                      // Server flag
      node.hasMoreData ||                                          // Server flag
      node.type === 'branch' ||                                   // Node type allows children
      node.type === 'folder' ||                                   // Folder type
      node.isExpandable                                            // Custom flag
    );
  };

// Update handleClosePopup
const handleClosePopup = () => {
  setPopupName("");
  setAnchorRef(null);
  setExpandedNodeInfo(null);
};
  

const renderSearchDynamicGroups = () => {
    if(popupName === "showDynamicGroupMenu" && expandedNodeInfo){
        return (
            <DocSearchDynamicGroup 
                isDynamicTrigger={true}
                dynamicTriigerAnchorRef={{ current: anchorRef }}
                level={expandedNodeInfo.level}
                nodePath={expandedNodeInfo.nodePath}
                dynamicGroupManager={dynamicGroupManager}
                onClose={handleClosePopup}
                />
        )
    }

    return null;
}

const createNodeId = (parentNodeId, rowKey) => {
  return parentNodeId ? `${parentNodeId}.${rowKey}` : rowKey;
};
  
  // Recursive function to render tree nodes
  const renderTreeNodes = (nodes, parentNodeId = '') => {
  const nodeArray = Array.isArray(nodes) ? nodes : Object.values(nodes);
  
  return nodeArray.map((node) => {
    const dynamicNodeId = createNodeId(parentNodeId, node.rowKey);
    const hasRealChildren = node.children && Object.keys(node.children).length > 0;
    const shouldForceExpandable = shouldShowExpandIcon(node) && !hasRealChildren;

// console.log("selected node:", dynamicNodeId, selectedNode);

    return (
      <StyledTreeItem
        key={dynamicNodeId}
        nodeId={dynamicNodeId}
        label={<DocSearchDynamicGroupChoiceRow viewData={node} selectedKey={dynamicNodeId === selectedNode ? 1 : 0} />}
        onLabelClick={handleNodeContentClick}
        forceExpandable={shouldForceExpandable}
      >
        {hasRealChildren && renderTreeNodes(node.children, dynamicNodeId)}
      </StyledTreeItem>
    );
  });
};
  

    return (
      <div className='uiDynamicGroupTreeContainer'>
        <div className='uiDynamicGroupColumn uiListScrollFullHeight'>
          <TreeView
            className={classes.root}
            expanded={expandedNodes}
            selected={selectedNode}
            onNodeToggle={handleNodeToggle}
            defaultCollapseIcon={<MinusSquare />}
            defaultExpandIcon={<PlusSquare />}
            defaultEndIcon={<CloseSquare />}
          >
            {renderTreeNodes(getRootNodes())}
          </TreeView>

          {renderSearchDynamicGroups()}
        </div>
    </div>
  );
}
